// Everyone in the aged care programme, in one list.
//
// Aged care participants are separated from the general Clients page (service_type decides which
// module a person belongs to), and the only place they used to appear was the funding register —
// which lists funding PLANS. So a participant created without a plan was invisible everywhere, and
// there was nowhere to answer "who is in this programme".
//
// The list leads with what stops each person being served or billed, because that is the reason to
// open it: no funding plan means nothing can be claimed for them, and no email means their monthly
// statement cannot be issued — and issuing one every month is enforceable.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { PersonAvatar } from '../../components/ui';
import { UserPlus, Users, Wallet, ClipboardList, Mail, Contact, Save } from 'lucide-react';
import {
  PageHead, Register, FigureStrip, Figure, Deadline, Currency, fmtDate, DetailDrawer, Field,
} from './design';
import { NewParticipantModal } from './Funding';

// Where each participant's quarterly budget stands, in the same words and colours as the overview.
const BUDGET = {
  over: { word: 'Over budget', text: 'text-red-800', bar: 'bg-red-500' },
  fast: { word: 'Will run out', text: 'text-amber-800', bar: 'bg-amber-500' },
  slow: { word: 'Underused', text: 'text-amber-800', bar: 'hue-bar opacity-50' },
  on_track: { word: 'On track', text: 'text-slate-600', bar: 'hue-bar' },
};

const SCHEME = { sah: 'Support at Home', hcp: 'Home Care Package' };

export default function AgedCareParticipants({ onGoTo, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  // The two identifiers: My Aged Care ("AC…") and the Services Australia care recipient ID (9
  // digits), which every claim line must carry.
  const [ids, setIds] = useState(null);
  const [savingIds, setSavingIds] = useState(false);
  const closeIds = useCallback(() => setIds(null), []);
  const saveIds = async () => {
    setSavingIds(true);
    try {
      await api.patch(`/aged-care/clients/${ids.id}`, { sa_care_recipient_id: ids.sa_id, aged_care_recipient_id: ids.mac });
      toast.success('Identifiers saved');
      setIds(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not save the identifiers'); }
    finally { setSavingIds(false); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.get('/aged-care/clients');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load participants');
      setRows([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // This quarter's budget for each participant (none for Home Care Packages or an unentered quarter).
  const [budget, setBudget] = useState({ byClient: {}, pace: 0 });
  useEffect(() => {
    api.get('/aged-care/budgets/summary').then((r) => {
      const q = r.data?.quarter;
      setBudget({
        byClient: Object.fromEntries((r.data?.list || []).map((b) => [b.clientId, b])),
        pace: q && q.daysTotal ? q.daysElapsed / q.daysTotal : 0,
      });
    }).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name || ''} ${r.mac || ''} ${r.email || ''}`.toLowerCase().includes(q));
  }, [rows, search]);

  // A CHSP participant is grant-funded: no funding plan, no Services Australia ID and no monthly
  // statement, so none of those is missing for them.
  const unfunded = rows.filter((r) => !r.funding_id && !r.chsp).length;
  const noEmail = rows.filter((r) => !r.email && !r.chsp).length;
  const noSaId = rows.filter((r) => !r.sa_id && !r.chsp).length;
  const onChsp = rows.filter((r) => r.chsp).length;

  const columns = [
    {
      key: 'name', header: 'Participant', sortable: true,
      render: (r) => (
        <span className="min-w-0 flex items-center gap-2.5">
          <PersonAvatar id={r.id} name={r.name} size="sm" />
          <span className="min-w-0 block">
            <span className="font-medium text-slate-900 block truncate">{r.name}</span>
            <span className={clsx('text-[12px]', !r.mac && r.chsp ? 'text-amber-700' : 'text-slate-500')}>
              {r.mac ? `My Aged Care ${r.mac}` : r.chsp ? 'No My Aged Care ID: DEX needs it' : 'No My Aged Care ID recorded'}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'funding', header: 'Funding', width: 200, sortable: true,
      sortValue: (r) => (r.funding_id ? `${r.scheme || ''}${r.level || ''}` : ''),
      render: (r) => (r.chsp
        ? (
          <span className="min-w-0 block">
            <span className="text-teal-800 font-medium block truncate">CHSP</span>
            {r.funding_id && <span className="text-[12px] text-amber-700">Also has a Support at Home plan</span>}
          </span>
        )
        : r.funding_id
        ? (
          <span className="min-w-0 block">
            <span className="text-slate-800 block truncate">
              {SCHEME[String(r.scheme || '').toLowerCase()] || r.scheme || 'Funded'}
              {r.level ? `, level ${r.level}` : ''}
            </span>
            {r.budget != null && <Currency value={r.budget} className="text-[12px] text-slate-500" />}
          </span>
        )
        // Not a neutral blank: without a plan nothing delivered to this person can be claimed.
        : <span className="text-red-800 font-medium">No funding plan</span>),
    },
    {
      key: 'quarter', header: 'Budget this quarter', width: 190, sortable: true,
      sortValue: (r) => (budget.byClient[r.id] ? budget.byClient[r.id].usedPct : -1),
      render: (r) => {
        const b = budget.byClient[r.id];
        if (!b) return <span className="text-slate-500">{r.chsp ? 'No budget (CHSP)' : r.funding_id && String(r.scheme || '').toUpperCase() !== 'HCP' ? 'Not entered' : '—'}</span>;
        const st = BUDGET[b.state] || BUDGET.on_track;
        const fill = Math.min(1, b.pool > 0 ? b.spent / b.pool : 0);
        return (
          <span className="block min-w-0">
            <span className="flex items-baseline justify-between gap-2 text-[12px]">
              <span className={clsx('font-medium', st.text)}>{st.word}</span>
              <span className="tabular-nums text-slate-500">{b.usedPct}%</span>
            </span>
            <span className="relative mt-1 block h-1.5 rounded-full bg-slate-100">
              <span className="absolute inset-0 overflow-hidden rounded-full">
                <span className={clsx('block h-full rounded-full', st.bar)} style={{ width: `${fill * 100}%` }} />
              </span>
              <span className="absolute -top-0.5 -bottom-0.5 w-0.5 rounded bg-slate-700/70" style={{ left: `calc(${budget.pace * 100}% - 1px)` }} aria-hidden="true" />
            </span>
          </span>
        );
      },
    },
    {
      key: 'care_plan', header: 'Care plan', width: 190, sortable: true,
      sortValue: (r) => r.review_due || (r.care_plan_id ? '9999' : ''),
      render: (r) => {
        if (!r.care_plan_id) return <span className="text-slate-500">None yet</span>;
        return r.review_due
          ? <Deadline date={r.review_due} absolute={false} />
          : <span className="text-slate-600">No review date</span>;
      },
    },
    {
      key: 'sa_id', header: 'Services Australia ID', width: 200, sortable: true,
      render: (r) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); setIds({ id: r.id, name: r.name, sa_id: r.sa_id || '', mac: r.mac || '' }); }}
          className="text-left hover:underline" aria-label={`Edit identifiers for ${r.name}`}>
          {r.sa_id
            ? <span className="font-mono text-[12px] text-slate-700">{r.sa_id}</span>
            : r.chsp ? <span className="text-slate-500">Not needed for CHSP</span>
              : <span className="text-amber-700">Not recorded: cannot be claimed</span>}
        </button>
      ),
    },
    {
      key: 'email', header: 'Statement goes to', width: 220,
      render: (r) => (r.email
        ? <span className="text-slate-600 truncate block">{r.email}</span>
        : r.chsp ? <span className="text-slate-500">No statement for CHSP</span>
          : <span className="text-amber-700">No email — cannot be sent</span>),
    },
    {
      key: 'date_of_birth', header: 'Date of birth', width: 130, sortable: true,
      render: (r) => <span className="text-slate-500 tabular-nums">{r.date_of_birth ? fmtDate(r.date_of_birth) : '—'}</span>,
    },
  ];

  return (
    <div>
      <PageHead icon={Users}
        title="Participants"
        purpose="Everyone in the aged care programme. They are kept separate from the general Clients list, and are still rostered on the same scheduler board as everyone else."
        actions={(
          <button type="button" onClick={() => setShowNew(true)} className="btn-primary">
            <UserPlus size={15} /> New participant
          </button>
        )}
      />

      <FigureStrip>
        <Figure label="Participants" value={rows.length} sub={onChsp ? `${onChsp} on CHSP` : null} />
        <Figure label="Without a funding plan" value={unfunded} tone={unfunded ? 'red' : 'slate'}
          sub={unfunded ? 'Nothing can be claimed for them' : 'All funded'} />
        <Figure label="Without an email" value={noEmail} tone={noEmail ? 'amber' : 'slate'}
          sub={noEmail ? 'Statements cannot be sent' : 'All contactable'} />
        <Figure label="Without a Services Australia ID" value={noSaId} tone={noSaId ? 'amber' : 'slate'}
          sub={noSaId ? 'Their visits cannot go in a claim file' : 'All can be claimed'} />
        <Figure label="With a care plan" value={rows.filter((r) => r.care_plan_id).length} />
      </FigureStrip>

      <Register
        title="Participants" count={filtered.length} countLabel="in the programme" icon={Users}
        search={search} onSearch={setSearch} searchPlaceholder="Search by name, My Aged Care ID or email"
        columns={columns} rows={filtered} rowKey="id"
        loading={loading} error={error} onRetry={load}
        onRowClick={onOpen ? (r) => onOpen(r.id) : undefined}
        rowTone={(r) => (r.chsp ? (!r.mac ? 'amber' : undefined) : !r.funding_id ? 'rose' : (!r.email || !r.sa_id) ? 'amber' : undefined)}
        rowActions={(r) => (
          <div className="flex items-center gap-1">
            <button type="button" title="Open funding" aria-label={`Open funding for ${r.name}`}
              onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(r.id, 'funding'); else onGoTo?.('funding'); }} className="btn-ghost btn-sm"><Wallet size={14} /></button>
            <button type="button" title="Open care plans" aria-label={`Open care plans for ${r.name}`}
              onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(r.id, 'careplan'); else onGoTo?.('careplans'); }} className="btn-ghost btn-sm"><ClipboardList size={14} /></button>
            <button type="button" title="Open statements" aria-label={`Open statements for ${r.name}`}
              onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(r.id, 'money'); else onGoTo?.('statements'); }} className="btn-ghost btn-sm"><Mail size={14} /></button>
          </div>
        )}
        empty={{
          icon: Users,
          title: 'No participants yet',
          hint: 'Add someone to the aged care programme, then give them a funding plan so their delivered care can be claimed.',
          action: <button type="button" onClick={() => setShowNew(true)} className="btn-primary btn-sm"><UserPlus size={14} /> New participant</button>,
        }}
        footer={rows.length
          ? 'A Support at Home participant needs a funding plan before anything delivered to them can be claimed, and an email before their monthly statement can be sent. A CHSP participant is enrolled on the CHSP screen instead.'
          : null}
      />

      {ids && (
        <DetailDrawer
          open
          onClose={closeIds}
          icon={Contact}
          width="sm"
          title={ids.name}
          subtitle="The identifiers this participant is known by"
          footer={(
            <>
              <button type="button" onClick={closeIds} className="btn-secondary">Cancel</button>
              <button type="button" onClick={saveIds} disabled={savingIds} className="btn-primary"><Save size={15} /> {savingIds ? 'Saving…' : 'Save'}</button>
            </>
          )}
        >
          <div className="space-y-4">
            <Field label="Services Australia care recipient ID" hint="The 9-digit number on the entry record in the Aged Care Provider Portal, for example 404006845. Every claim line needs it.">
              {(id) => <input id={id} inputMode="numeric" className="input-field font-mono" value={ids.sa_id} onChange={(e) => setIds((p) => ({ ...p, sa_id: e.target.value.replace(/[^0-9]/g, '') }))} placeholder="404006845" />}
            </Field>
            <Field label="My Aged Care ID" hint='Starts with "AC". Used in My Aged Care, not in claims.'>
              {(id) => <input id={id} className="input-field font-mono" value={ids.mac} onChange={(e) => setIds((p) => ({ ...p, mac: e.target.value }))} placeholder="AC12345678" />}
            </Field>
          </div>
        </DetailDrawer>
      )}

      {showNew && (
        <NewParticipantModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); toast.success('Add a funding plan next so their care can be claimed'); }}
        />
      )}
    </div>
  );
}
